import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import apiRoutes from "../constants/api-routes";
import messageLevels from "../constants/message-levels";
import messages from "../constants/messages";
import pages from "../constants/pages";
import { selectOrganizationId } from "../redux/userSlice";

import useAuthHeader from "./useAuthHeader";
import useToastAlert from "./useToastAlert";


/**
 * Rule Wizard Hook
 * @param {*} editedId 
 * @returns exposed rule wizard state and data persistence methods
 */
export default function useRuleWizard(editedId) {
	/**
	 * Rule Wizard State
	 */
	const [activeStep, setActiveStep] = useState(0);
	const [isRuleLoading, setIsRuleLoading] = useState(false);
	const [stepDataHasChanged, setStepDataHasChanged] = useState(false);
	const [stepOneErrors, setStepOneErrors] = useState({});
	const [wizardFormError, setWizardFormError] = useState("");
	const [formulaError, setFormulaError] = useState("");
	const [isSavingProgress, setIsSavingProgress] = useState({ isSaving: true, lastSaved: null });
	const [draftRuleId, setDraftRuleId] = useState(null);
	const [active, setActive] = useState(false);
	const [editedVersionOfRule, setEditedVersionOfRule] = useState({});
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [ruleLocation, setRuleLocation] = useState("");
	const [reactions, setReactions] = useState([]);
	const [triggers, setTriggers] = useState([]);
	const [formula, setFormula] = useState([]);
	const [groups, setGroups] = useState([]);
	const [tags, setTags] = useState([]);
	const [itemsInFormula, setItemsInFormula] = useState([]);
	const [stepsCompleted, setStepsCompleted] = useState({});
	const [advancedTriggers, setAdvancedTriggers] = useState([]);
	const [platformOptions, setPlatformOptions] = useState([]);
	const [platformId, setPlatformId] = useState("");

	/**
	 * Hook instances
	 */
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();

	/**
	 * Redux state
	 */
	const currentUserOrganizationId = useSelector(selectOrganizationId);

	/**
	 * Rule Wizard Methods
	 */
	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleComplete = (step) => {
		handleSaveChanges();
		if (activeStep === 4) {
			navigate(pages.home);
		} else {
			handleStepChange(step, false, "");
		}
	};

	const handleExitEditing = async () => {
		if (!isRuleLoading && name !== "" && description !== "") {
			handleSaveChanges();
		}

		navigate(pages.home);
		setStepDataHasChanged(false);
	};

	/**
	 * On load, determine which steps are complete already. 
	 * This applied to editing rules only.
	 */
	const handleLoadCompletedSteps = (rule) => {
		let newStepsCompleted = { ...stepsCompleted };
		newStepsCompleted[0] = rule.name !== "" && rule.description !== "" ? true : false;
		newStepsCompleted[1] = rule.rulereactions.length > 0 ? true : false;
		newStepsCompleted[2] = rule.ruletriggers.length > 0 || rule.groups.length > 0 ? true : false;
		newStepsCompleted[3] = rule.formula?.formula?.length > 0 || rule.groups.length > 0 ? true : false;
		newStepsCompleted[4] = rule.groups.length > 0 ? true : false;
		setStepsCompleted(newStepsCompleted);
	};

	/**
	* Step Progression
	* Sets the previous step as completed.
	*/
	const handleStepProgression = () => {
		let newStepsCompleted = { ...stepsCompleted };
		newStepsCompleted[activeStep] = true;
		setStepsCompleted(newStepsCompleted);
	};

	const handleNext = async (step = null) => {
		let hasErrors = false;
		let formError = "";
		switch (activeStep) {
			case 0:
				hasErrors = await doesStepOneHaveErrors();
				if (!hasErrors) {
					setWizardFormError("");
					handleSaveChanges();
					initAdvancedTriggers(platformId);
				}
				break;
			case 1:
				if (ruleLocation === "") {
					formError = messages.RULE_LOCATION_REQUIRED_ERROR_MSG;
					hasErrors = true;
				} else if (reactions.length === 0 && ruleLocation !== "") {
					formError = messages.RULE_REACTION_REQUIRED_ERROR_MSG;
					hasErrors = true;
				} else {
					handleSaveChanges();
				}
				break;
			case 2:
				handleSaveChanges();
				break;
			case 3:
				handleSaveChanges();
				break;
			default:
				break;
		}
		handleStepChange(step, hasErrors, formError);
	};

	/**
	 * Handle step changes from the step button click or next button click.
	 * @param {*} step step being moved to
	 * @param {*} hasErrors if there are errors on a step that requires validation
	 * @param {*} formError error message to display if there are errors
	 */
	const handleStepChange = (step, hasErrors, formError) => {
		/**
		 * Block moving to higher steps if there are errors
		 */
		if (hasErrors && step > activeStep) {
			setWizardFormError(formError);
			return;
		}
		
		/**
		 * Move up one step if stepDiff is 1
		 */
		const stepDiff = step - activeStep;
		if (stepDiff === 1) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
			handleStepProgression();
			return;
		}
		/**
		 * Move up to passed in step if stepDiff is larger than 1
		 * Handles edge case where user jumps ahead to step 3 or 4 without completing step 2.
		 */
		if (stepDiff > 1) {
			setActiveStep(step);
			handleStepProgression();
			setWizardFormError("");
			return;
		}
		
		/**
		 * Set the target step if:
		 * - there are no errors
		 * - OR we're moving to a lower step
		 */
		if (!hasErrors || step < activeStep) {
			setActiveStep(step);
			handleStepProgression();
			return;
		}
	};

	/**
	 * Next and/or Finish Button is pressed.
	 * Save Progress at each step.
	 * 
	 * 1. Check for errors
	 * 2. Save Progress
	 */
	const handleSubmit = (step = null) => {
		switch (activeStep) {
			case 0:
				handleNext(step);
				break;
			case 1:
				handleNext(step);
				break;
			case 2:
				handleNext(step);
				break;
			case 3:
				handleNext(step);
				break;
			case 4:
				handleNext(step);
				break;
			default:
				break;
		}
	};

	/**
	 * Save rule after every step.
	 * If this is a new rule, create new rule, otherwise update the existing one
	*/
	const handleSaveChanges = async (updatedTriggers = triggers, updatedTags = tags, updatedReactions = reactions) => {
		let ruleJSON = {
			active: active,
			description: description,
			formula: formula,
			groups: groups,
			isDraft: false,
			name: name,
			reactions: updatedReactions ? updatedReactions : reactions,
			stepUpdated: activeStep,
			tags: updatedTags ? updatedTags : tags,
			triggers: updatedTriggers !== null ? updatedTriggers : triggers,
			ruleLocation: ruleLocation,
			platformId: platformId,
		};

		let ruleId = editedId ? editedId : draftRuleId;
		if (ruleId) {
			axios.put(apiRoutes.updateRule(ruleId), ruleJSON, { headers: authHeader }
			).then(() => {
				setIsSavingProgress({ isSaving: true, lastSaved: new Date() });
				handleToastAlert(messageLevels.SUCCESS, messages.RULE_UPDATE_SUCCESS_MSG);
			}).catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_UPDATE_ERROR_MSG);
			});
		} else {
			axios.post(apiRoutes.createRule, ruleJSON, {
				headers: authHeader
			}).then(res => {
				setDraftRuleId(res.data.id);
				setIsSavingProgress({ isSaving: true, lastSaved: new Date() });
				handleToastAlert(messageLevels.SUCCESS, messages.RULE_CREATED_SUCCESS_MSG);
			}).catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_CREATION_ERROR_MSG);
			});
		}
	};

	/**
	 * Retrieve Rule Metadata for editing.
	 * Additional step to retrieve Triggers that are not in the formula.
	 */
	const initEditingRule = () => {
		// TODO: maintain rule id in a var for edit and new so we don"t have to compare id and newRuleId
		setIsRuleLoading(true);
		axios.get(apiRoutes.getRule(editedId), {
			headers: authHeader
		}).then(res => {
			setActive(res.data.active);
			setDescription(res.data.description);
			setEditedVersionOfRule(res.data);
			setFormula(res.data.formula);
			setGroups(res.data.groups);
			setItemsInFormula(res.data.formula?.formula || []);
			setName(res.data.name);
			setReactions(res.data.rulereactions);
			setTags(res.data.tags);
			setTriggers(res.data.ruletriggers);
			setRuleLocation(res.data.ruleLocation);
			handleLoadCompletedSteps(res.data);
			setIsRuleLoading(false);
			setPlatformId(res.data.platformId);
			initAdvancedTriggers(res.data.platformId);
		}).catch(err => {
			console.log(err);
			setIsRuleLoading(false);
		});
	};

	/**
	 * Retrieve Advanced Triggers for the Formula Step.
	 */
	const initAdvancedTriggers = (platformId) => {
		axios.get(apiRoutes.getAdvancedTriggers(currentUserOrganizationId, platformId), {
			headers: authHeader
		}).then(res => {
			setAdvancedTriggers(res.data);
		}).catch(err => {
			console.log(err);
		});
	};

	const initPlatforms = () => {
		axios.get(apiRoutes.getPlatforms(currentUserOrganizationId), {
			headers: authHeader
		}).then(res => {
			const platforms = res.data.map(platformOrganization => ({id: platformOrganization.platform.id, name: platformOrganization.platform.name, active: platformOrganization.active}));
			const activePlatforms = platforms.filter(platform => platform.active);
			setPlatformOptions(activePlatforms);
			if (activePlatforms.length === 1) {
				setPlatformId(activePlatforms[0].id);
			}
		}).catch(err => {
			console.log(err);
		});
	};

	const checkRuleNameUniqueness = async (valName) => {
		let errors = {};
		let data;
		if (valName === "") {
			errors = { nameError: true, nameErrorMessage: messages.FIELD_IS_REQUIRED_ERROR_MSG };
			return errors;
		}
		await axios.get(apiRoutes.getRuleByName, {
			headers: authHeader,
			params: { name: valName }
		}).then((res) => {
			data = res.data;
			if (res.data) {
				errors = { nameError: true, nameErrorMessage: messages.RULE_NAME_UNIQUE_ERROR_MSG };
			}
		}).catch(err => {
			console.log(err);
			return null;
		});
		return [data, errors];
	};

	/**
	 * Wizard Step 1 Form Rule
	 * 1. Name is required
	 * 2. Description is required
	 * 3. Name must be unique
	 * TODO: *All* error detection should be here
	 */
	const doesStepOneHaveErrors = async () => {
		let errors = {};
		let hasErrors = false;
		setStepOneErrors(errors);

		if (name === "") {
			errors = { nameError: true, nameErrorMessage: messages.FIELD_IS_REQUIRED_ERROR_MSG };
			setStepOneErrors(errors);
			hasErrors = true;
		}

		if (description === "") {
			errors = { ...errors, descriptionError: true, descriptionErrorMessage: messages.FIELD_IS_REQUIRED_ERROR_MSG };
			setStepOneErrors(errors);
			hasErrors = true;
		}

		if (platformId === "") {
			errors = { ...errors, platformError: true, platformErrorMessage: messages.FIELD_IS_REQUIRED_ERROR_MSG };
			setStepOneErrors(errors);
			hasErrors = true;
		}

		try {
			let ruleNameRes = await checkRuleNameUniqueness(name);
			if (ruleNameRes[0]) {
				if (editedId || draftRuleId) {
					if (name !== editedVersionOfRule.name) {
						if (draftRuleId) {
							if (ruleNameRes[0].id === parseInt(draftRuleId)) {
								errors = { ...errors };
							} else {
								errors = { ...errors, ...ruleNameRes[1] };
								hasErrors = true;
							}
						} else {
							errors = { ...errors, ...ruleNameRes[1] };
							hasErrors = true;
						}
					}
				} else {
					errors = { ...errors, ...ruleNameRes[1] };
					hasErrors = true;
				}
			}
		} catch (e) {
			console.log(e);
		}

		if (!errors["nameError"]) {
			setStepOneErrors({});
		}

		setStepOneErrors(errors);

		return hasErrors;
	};

	/**
	 * Basic formula validation.
	 * We are validating the formulas parenthesis count only.
	 */
	const validateFormula = () => {
		const [hasParenthesesError, openings, closings] = validateParenthesesCount(formula);
		const updatedErrors = [];
		if (hasParenthesesError) {
			updatedErrors.push(messages.RULE_PARENTHESES_ERROR(openings, closings));
		}
		if (updatedErrors.length > 0) {
			setFormulaError(updatedErrors);
		} else {
			setFormulaError([]);
		}
	};

	/**
	 * Validate the parentheses count of the formula.
	 * @param {*} formula 
	 * @returns boolean representing if the formulas parentheses count is the same.
	 */
	const validateParenthesesCount = (formula) => {
		const openings = formula?.formula?.filter(item => item.triggerName === "(");
		const closings = formula?.formula?.filter(item => item.triggerName === ")");
		const validation = [false, openings, closings];
	
		if (openings?.length !== closings?.length) {
			validation[0] = true;
		}
		return validation;
	};

	const getSteps = () => {
		return [
			{ "num": 0, "desc": "Name the Rule" },
			{ "num": 1, "desc": "Define Location and Reactions" },
			{ "num": 2, "desc": "Define Triggers (Optional)" },
			{ "num": 3, "desc": "Define the Formula (Optional)" },
			{ "num": 4, "desc": "Confirm and Assign Groups" }
		];
	};

	return {
		// Expose rule wizard state
		activeStep, setActiveStep, 
		isRuleLoading, setIsRuleLoading, 
		stepDataHasChanged, setStepDataHasChanged,
		stepOneErrors, setStepOneErrors, 
		wizardFormError, setWizardFormError, 
		formulaError, setFormulaError,
		isSavingProgress, setIsSavingProgress,
		draftRuleId, setDraftRuleId, 
		active, setActive,
		editedVersionOfRule, setEditedVersionOfRule, 
		name, setName,
		description, setDescription,
		ruleLocation, setRuleLocation,
		reactions, setReactions,
		triggers, setTriggers,
		formula, setFormula,
		groups, setGroups,
		tags, setTags,
		itemsInFormula, setItemsInFormula,
		stepsCompleted, setStepsCompleted,
		advancedTriggers, setAdvancedTriggers,
		platformOptions, setPlatformOptions,
		platformId, setPlatformId,
		editedId,

		//Expose rule wizard methods
		handleBack,
		handleComplete,
		handleExitEditing,
		handleLoadCompletedSteps,
		handleStepProgression,
		handleNext,
		handleSubmit,
		handleSaveChanges,
		initEditingRule,
		initAdvancedTriggers,
		initPlatforms,
		validateFormula,
		getSteps
	};
}